// ============================================
// COMPONENT - Modal de Checkout de Venta
// ============================================

import { useState, useEffect, useRef } from 'react';
import {
  useCartStore,
  useAuthStore,
  useUIStore,
  useDashboardKpis,
  TipoPago,
  useBuscarClientesFiados,
  useCrearClienteFiado,
  useActualizarClienteFiado,
  useCrearVentaPos,
  ventasRepository,
  clienteRepository,
  type ClienteFiadoResult,
  type ProductoVentaDto,
  type CrearVentaPosDto,
  type CrearVentaPosResponse,
  type VentaItemResponse,
  type VentaErrorResponse,
  type ProductoVendible
} from '@almacenes/core';
import { GiWallet } from "react-icons/gi";
import { MdClose, MdEdit, MdCheck, MdSearch, MdKeyboardArrowDown, MdAdd, MdDelete } from 'react-icons/md';
import { FaMoneyBillWave, FaCreditCard, FaExchangeAlt, FaCrown } from 'react-icons/fa';
import { FaHandHoldingDollar } from 'react-icons/fa6';
import { SiVisa } from 'react-icons/si';
import { formatRut, cleanRut, validateRut } from '@/hooks/useRutFormatter';
import { useQueryClient } from '@tanstack/react-query';
import { PremiumRequiredModal } from '@/components/PremiumRequiredModal';
import { redondearEfectivo } from '@/utils/redondeo';

type TipoRecibo = 'VOUCHER' | 'BOLETA';

interface ModalCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  onSaleCompleted?: (boletaId: number, shouldPrint: boolean) => void;
}

export const ModalCheckout = ({ isOpen, onClose, onSaleCompleted }: ModalCheckoutProps) => {
  const session = useAuthStore((state) => state.session);
  const addToast = useUIStore((state) => state.addToast);
  const total = useCartStore((state) => state.getTotal());
  const clearCart = useCartStore((state) => state.clearCart);
  const cartItems = useCartStore((state) => state.items);
  const queryClient = useQueryClient();
  
  // Obtener datos de folios y certificado
  const { data: kpis } = useDashboardKpis();

  // Hooks para gestión de clientes y ventas
  const buscarClientesMutation = useBuscarClientesFiados();
  const crearClienteMutation = useCrearClienteFiado();
  const actualizarClienteMutation = useActualizarClienteFiado();
  const crearVentaMutation = useCrearVentaPos();

  // Ref para input de monto recibido
  const montoRecibidoInputRef = useRef<HTMLInputElement>(null);

  // Ref para acceder a handleFinalizar desde el useEffect de teclado
  const handleFinalizarRef = useRef<(imprimir: boolean) => void>(() => {});

  const [tipoPago, setTipoPago] = useState<TipoPago>(TipoPago.EFECTIVO);
  const [montoRecibido, setMontoRecibido] = useState<string>('0');
  const [tipoRecibo, setTipoRecibo] = useState<TipoRecibo>('VOUCHER');
  const [emailCliente, setEmailCliente] = useState<string>('');  
  const [isFinalizando, setIsFinalizando] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailError, setEmailError] = useState<string>('');

  // Estados para modo multi-pago
  interface Payment {
    id: number;
    methodPayment: TipoPago;
    amount: string;
    fiadoClienteId?: number;
    cliente?: ClienteFiadoResult | null;
  }
  const [isMultiPaymentMode, setIsMultiPaymentMode] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [nextPaymentId, setNextPaymentId] = useState(1);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [montoRecibidoEfectivo, setMontoRecibidoEfectivo] = useState<string>('0');

  // Estados para gestión de clientes fiados
  const [clienteSeleccionado, setClienteSeleccionado] = useState<ClienteFiadoResult | null>(null);
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
  const [clientesBuscados, setClientesBuscados] = useState<ClienteFiadoResult[]>([]);
  
  // Filtro de búsqueda unificado
  const [busquedaTexto, setBusquedaTexto] = useState('');
  
  // Formulario de cliente (crear/editar)
  const [formNombre, setFormNombre] = useState('');
  const [formTelefono, setFormTelefono] = useState('');
  const [formRut, setFormRut] = useState('');
  const [formRutFormateado, setFormRutFormateado] = useState('');
  const [formAlias, setFormAlias] = useState('');

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setTipoPago(TipoPago.EFECTIVO);
      setMontoRecibido('0');
      setTipoRecibo('VOUCHER');
      setEmailCliente('');
      setShowEmailInput(false);
      // Reset multi-pago
      setIsMultiPaymentMode(false);
      setPayments([]);
      setNextPaymentId(1);
      setShowPremiumModal(false);
      setMontoRecibidoEfectivo('0');
      // Reset cliente
      setClienteSeleccionado(null);
      setMostrarModalCrear(false);
      setMostrarModalEditar(false);
      setClientesBuscados([]);
      resetBusqueda();
      resetFormulario();
    }
  }, [isOpen]);

  // Focus en input de monto recibido cuando se abre el modal o cambia a efectivo
  useEffect(() => {
    if (isOpen && tipoPago === TipoPago.EFECTIVO) {
      setTimeout(() => {
        montoRecibidoInputRef.current?.focus();
        montoRecibidoInputRef.current?.select();
      }, 100);
    }
  }, [isOpen, tipoPago]);

  // Habilitar tecla Enter para finalizar venta
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (mostrarModalCrear || mostrarModalEditar || showPremiumModal) return;
      if (isFinalizando) return;
      
      // Validación para modo multi-pago
      if (isMultiPaymentMode) {
        if (payments.length === 0) return;
        if (Math.abs(calcularRestante()) > 2) return;
        if (payments.some(p => p.methodPayment === TipoPago.FIADO && !p.fiadoClienteId)) return;
      } else {
        // Validación para modo single
        if (tipoPago === TipoPago.FIADO && !clienteSeleccionado) return;
      }
      
      // No disparar si el foco está en otro input (email, búsqueda de cliente, etc.)
      const active = document.activeElement;
      const isTypingElsewhere =
        (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) &&
        active !== montoRecibidoInputRef.current;
      if (isTypingElsewhere) return;
      e.preventDefault();
      handleFinalizarRef.current(true);
    };

    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mostrarModalCrear, mostrarModalEditar, showPremiumModal, isFinalizando, isMultiPaymentMode, payments, tipoPago, clienteSeleccionado]);

  if (!isOpen) return null;

  // Validar formato de email
  const validarEmail = (email: string): boolean => {
    if (!email) return true; // Email vacío es válido (opcional)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Manejar cambio de email con validación
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailCliente(value);
    
    if (value && !validarEmail(value)) {
      setEmailError('Formato de correo inválido');
    } else {
      setEmailError('');
    }
  };

  // Funciones para gestión de clientes
  const resetBusqueda = () => {
    setBusquedaTexto('');
    setClientesBuscados([]);
  };

  const resetFormulario = () => {
    setFormNombre('');
    setFormTelefono('');
    setFormRut('');
    setFormRutFormateado('');
    setFormAlias('');
  };

  const handleBusquedaTextoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBusquedaTexto(value);
    
    // Auto-buscar cuando tenga al menos 3 caracteres
    if (value.length >= 3) {
      handleBuscarClientes(value);
    } else {
      setClientesBuscados([]);
    }
  };

  const handleFormRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cleaned = cleanRut(inputValue);
    const limited = cleaned.slice(0, 9);
    setFormRut(limited);
    setFormRutFormateado(formatRut(limited));
  };

  const handleBuscarClientes = async (texto: string = busquedaTexto) => {
    if (!texto || texto.length < 3) return;

    try {
      const dto: any = {};
      
      // Detectar automáticamente el tipo de búsqueda
      const textoLimpio = texto.trim();
      
      // Si contiene letras, buscar por nombre
      if (/[a-zA-ZáéíóúÁÉÍÓÚñÑ]/.test(textoLimpio)) {
        dto.nombre = textoLimpio;
      }
      // Si solo tiene números y son exactamente 9, buscar por teléfono
      else if (/^\d{9}$/.test(textoLimpio)) {
        dto.telefono = textoLimpio;
      }
      // Si tiene números y guion, validar y buscar por RUT
      else if (textoLimpio.includes('-') || /^\d+$/.test(textoLimpio)) {
        const rutLimpio = cleanRut(textoLimpio);
        if (rutLimpio.length >= 8 && validateRut(rutLimpio)) {
          const rut = rutLimpio.slice(0, -1);
          const dv = rutLimpio.slice(-1);
          dto.rutDv = `${rut}-${dv}`;
        } else {
          // Si no es un RUT válido, intentar buscar por nombre de todas formas
          dto.nombre = textoLimpio;
        }
      }
      // Si solo son números pero menos de 9, buscar por nombre
      else {
        dto.nombre = textoLimpio;
      }

      const resultados = await buscarClientesMutation.mutateAsync(dto);
      setClientesBuscados(resultados);
    } catch (error) {
      console.error('Error al buscar clientes:', error);
      setClientesBuscados([]);
    }
  };

  const handleSeleccionarCliente = (cliente: ClienteFiadoResult) => {
    setClienteSeleccionado(cliente);
    resetBusqueda();
    addToast('success', `Cliente ${cliente.nombre} seleccionado`);
  };

  const handleEditarCliente = () => {
    if (!clienteSeleccionado) return;

    setFormNombre(clienteSeleccionado.nombre);
    setFormTelefono(clienteSeleccionado.telefono);
    setFormAlias(clienteSeleccionado.alias || '');
    const rutCompleto = clienteSeleccionado.rut
      ? `${clienteSeleccionado.rut}${clienteSeleccionado.dv}`
      : '';
    setFormRut(rutCompleto);
    setFormRutFormateado(formatRut(rutCompleto));
    setMostrarModalEditar(true);
  };

  const handleCambiarCliente = () => {
    setClienteSeleccionado(null);
    resetBusqueda();
    resetFormulario();
    setClientesBuscados([]);
  };

  const handleCrearCliente = async () => {
    // Validaciones
    if (!formNombre.trim() || formNombre.trim().length < 3) {
      addToast('error', 'El nombre debe tener al menos 3 caracteres');
      return;
    }

    if (formTelefono.length !== 9) {
      addToast('error', 'El teléfono debe tener 9 dígitos');
      return;
    }

    // Validar RUT solo si se ingresó algo
    if (formRut && !validateRut(formRut)) {
      addToast('error', 'El RUT ingresado no es válido');
      return;
    }

    try {
      const dto: any = {
        nombre: formNombre.trim(),
        telefono: formTelefono,
      };
      
      // Solo incluir RUT si se ingresó
      if (formRut) {
        const rut = formRut.slice(0, -1);
        const dv = formRut.slice(-1);
        dto.rutDv = `${rut}-${dv}`;
      }

      const nuevoCliente = await crearClienteMutation.mutateAsync(dto);
      setClienteSeleccionado(nuevoCliente);
      setMostrarModalCrear(false);
      resetFormulario();
      addToast('success', 'Cliente creado exitosamente');
    } catch (error) {
      addToast('error', 'Error al crear cliente');
      console.error(error);
    }
  };

  const handleActualizarCliente = async () => {
    if (!clienteSeleccionado) return;

    // Validaciones
    if (!formNombre.trim() || formNombre.trim().length < 3) {
      addToast('error', 'El nombre debe tener al menos 3 caracteres');
      return;
    }

    if (formTelefono.length !== 9) {
      addToast('error', 'El teléfono debe tener 9 dígitos');
      return;
    }

    // Validar RUT solo si se ingresó algo
    if (formRut && !validateRut(formRut)) {
      addToast('error', 'El RUT ingresado no es válido');
      return;
    }

    try {
      const dto: any = {
        clienteId: clienteSeleccionado.clienteId,
        nombre: formNombre.trim(),
        telefono: formTelefono,
      };
      
      // Solo incluir RUT si se ingresó
      if (formRut) {
        const rut = formRut.slice(0, -1);
        const dv = formRut.slice(-1);
        dto.rutDv = `${rut}-${dv}`;
      }

      await actualizarClienteMutation.mutateAsync(dto);

      // Guardar alias si cambió
      if (clienteSeleccionado.clienteAlmacenId) {
        if (formAlias.trim()) {
          await clienteRepository.setNombreClienteAlmacen(clienteSeleccionado.clienteAlmacenId, formAlias.trim());
        } else if (clienteSeleccionado.alias) {
          // Si había alias y ahora está vacío, eliminarlo
          await clienteRepository.deleteNombreClienteAlmacen(clienteSeleccionado.clienteAlmacenId);
        }
      }

      // Actualizar cliente seleccionado con los nuevos datos
      const clienteActualizado: ClienteFiadoResult = {
        ...clienteSeleccionado,
        nombre: formNombre.trim(),
        telefono: formTelefono,
        alias: formAlias.trim() || null,
      };

      // Solo actualizar RUT si se proporcionó
      if (formRut) {
        const rut = formRut.slice(0, -1);
        const dv = formRut.slice(-1);
        clienteActualizado.rut = parseInt(rut);
        clienteActualizado.dv = dv;
      }

      setClienteSeleccionado(clienteActualizado);
      setMostrarModalEditar(false);
      resetFormulario();
      addToast('success', 'Cliente actualizado exitosamente');
    } catch (error) {
      addToast('error', 'Error al actualizar cliente');
      console.error(error);
    }
  };

  // Calcular total con redondeo si es efectivo (solo en modo single)
  const totalAPagar = (!isMultiPaymentMode && tipoPago === TipoPago.EFECTIVO) 
    ? redondearEfectivo(total) 
    : total;
  const vuelto = parseFloat(montoRecibido) - totalAPagar;

  const handleTipoPagoClick = (tipo: TipoPago) => {
    setTipoPago(tipo);
    
    // Si no es efectivo, autocomplete el monto recibido
    if (tipo !== TipoPago.EFECTIVO && tipo !== TipoPago.FIADO) {
      setMontoRecibido(total.toString());
    }

    // Si cambia de FIADO a otro, resetear cliente
    if (tipo !== TipoPago.FIADO) {
      setClienteSeleccionado(null);
      setMostrarModalCrear(false);
      setMostrarModalEditar(false);
      setClientesBuscados([]);
    }
  };

  // Formatear número con separador de miles (punto)
  const formatearMonto = (valor: string): string => {
    // Eliminar todo excepto números
    const soloNumeros = valor.replace(/\D/g, '');
    
    if (soloNumeros === '') return '';
    
    // Convertir a número y formatear con separador de miles
    return parseInt(soloNumeros).toLocaleString('es-CL');
  };

  // Manejar cambio en input de monto recibido
  const handleMontoRecibidoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value;
    // Eliminar todo excepto números
    const soloNumeros = valor.replace(/\D/g, '');
    setMontoRecibido(soloNumeros);
  };

  const handleFinalizar = async (imprimir: boolean) => {
    // Validaciones para modo multi-pago
    if (isMultiPaymentMode) {
      // Validar que haya al menos 1 pago
      if (payments.length === 0) {
        addToast('error', 'Debes agregar al menos un medio de pago');
        return;
      }

      // Validar que no haya más de 1 FIADO
      const fiadosCount = payments.filter(p => p.methodPayment === TipoPago.FIADO).length;
      if (fiadosCount > 1) {
        addToast('error', 'Solo puede haber máximo 1 pago fiado por venta');
        return;
      }

      // Validar que todos los FIADO tengan cliente
      const fiadosSinCliente = payments.some(p => p.methodPayment === TipoPago.FIADO && !p.fiadoClienteId);
      if (fiadosSinCliente) {
        addToast('error', 'Debes seleccionar un cliente para el pago fiado');
        return;
      }

      // Validar que los pagos en efectivo cumplan con la ley de redondeo
      const efectivosSinRedondear = payments.filter(p => {
        if (p.methodPayment === TipoPago.EFECTIVO) {
          const monto = parseInt(p.amount) || 0;
          return monto % 10 !== 0 && monto > 0;
        }
        return false;
      });
      
      if (efectivosSinRedondear.length > 0) {
        const montosInvalidos = efectivosSinRedondear.map(p => `$${parseInt(p.amount).toLocaleString('es-CL')}`).join(', ');
        addToast('error', `Los montos en efectivo deben cumplir con la ley de redondeo (múltiplos de 10). Montos inválidos: ${montosInvalidos}`);
        return;
      }

      // Validar que la suma de pagos coincida con el total (tolerancia ±2)
      const restante = calcularRestante();
      if (Math.abs(restante) > 2) {
        addToast('error', `La suma de los pagos debe igualar el total. Restante: $${restante.toLocaleString('es-CL')}`);
        return;
      }
    } else {
      // Validación para modo single-pago
      if (tipoPago === TipoPago.FIADO && !clienteSeleccionado) {
        addToast('error', 'Debes seleccionar un cliente para venta fiada');
        return;
      }
    }

    setIsFinalizando(true);

    try {
      // Construir array de productos (igual para ambos modos)
      const products: ProductoVentaDto[] = [];

      for (const item of cartItems) {
        // Si es un pack, expandir sus productos. El servidor recalcula el total real de
        // cada línea a partir de la receta del pack (packId + packQuantity); discount/total
        // se siguen mandando por compatibilidad pero se ignoran para estas líneas.
        if (item.packDetalle) {
          for (const packProduct of item.packDetalle.products) {
            products.push({
              productoAlmacenId: packProduct.productoAlmacenId,
              nombre: packProduct.nombre,
              // El backend valida discount >= 0 (CreateSaleProductRequest) y de todas formas
              // lo ignora para líneas de pack (usa la receta actual, no lo enviado acá).
              // Cuando el pack es más caro que sus partes, packProduct.discount es negativo.
              discount: Math.max(0, packProduct.discount) * item.cantidad,
              quantity: packProduct.quantity * item.cantidad,
              total: packProduct.total * item.cantidad,
              packId: item.packDetalle.packId,
              packQuantity: item.cantidad,
            });
          }
        }
        // Si es un producto de calculadora (sin productoAlmacenId real)
        else if (item.producto.productoId === -1) {
          products.push({
            productoAlmacenId: null,
            nombre: item.producto.productoNombre,
            discount: item.descuento || 0,
            quantity: item.cantidad,
            salePrice: item.producto.precioVentaActual || 0,
            sellBy: item.producto.venderPor,
            total: item.subtotal,
          });
        }
        // Producto normal
        else {
          products.push({
            productoAlmacenId: item.producto.productoAlmacenId,
            nombre: item.producto.productoNombre,
            discount: item.descuento || 0,
            quantity: item.cantidad,
            total: item.subtotal,
          });
        }
      }

      let response: CrearVentaPosResponse;

      // Decidir qué endpoint usar según cantidad de pagos
      if (isMultiPaymentMode && payments.length >= 2) {
        // Usar endpoint multi-payment
        const ventaMultiDto: any = {
          products,
          payments: payments.map(p => ({
            methodPayment: p.methodPayment,
            amount: parseInt(p.amount) || 0,
            ...(p.fiadoClienteId && { fiadoClienteId: p.fiadoClienteId }),
          })),
          billingType: tipoRecibo === 'BOLETA' ? 39 : null,
          finalPrice: totalAPagar,
        };

        response = await ventasRepository.crearVentaMultiPago(ventaMultiDto);
      } else {
        // Usar endpoint single-payment (modo normal o multi con 1 solo pago)
        const ventaDto: CrearVentaPosDto = {
          methodPayment: isMultiPaymentMode ? payments[0].methodPayment : tipoPago,
          billingType: tipoRecibo === 'BOLETA' ? 39 : null,
          finalPrice: totalAPagar,
          products,
        };

        // Agregar clienteId si es FIADO
        if (isMultiPaymentMode) {
          if (payments[0].methodPayment === TipoPago.FIADO && payments[0].fiadoClienteId) {
            ventaDto.fiadoClienteId = payments[0].fiadoClienteId;
          }
        } else {
          if (tipoPago === TipoPago.FIADO && clienteSeleccionado) {
            ventaDto.fiadoClienteId = clienteSeleccionado.clienteId;
          }
        }

        response = await crearVentaMutation.mutateAsync(ventaDto);
      }

      // Actualizar stock de productos en memoria
      const updateStockInContent = (content: ProductoVendible[], productoAlmacenId: number, stock: number) =>
        content.map((producto) =>
          producto.productoAlmacenId === productoAlmacenId ? { ...producto, stock } : producto
        );

      response.items.forEach((item: VentaItemResponse) => {
        if (item.productoAlmacenId !== null) {
          queryClient.setQueriesData(
            { queryKey: ['productos', 'sellable'] },
            (oldData: any) => {
              // Forma "cargar más" (useInfiniteQuery): { pages: [{ content, hasNext, page }] }
              if (oldData?.pages) {
                return {
                  ...oldData,
                  pages: oldData.pages.map((page: { content: ProductoVendible[] }) => ({
                    ...page,
                    content: updateStockInContent(page.content, item.productoAlmacenId!, item.stockCurrent),
                  })),
                };
              }

              // Forma paginada clásica (Page): { content: [...] }
              if (!oldData?.content) return oldData;

              return {
                ...oldData,
                content: updateStockInContent(oldData.content, item.productoAlmacenId!, item.stockCurrent),
              };
            }
          );
        }
      });

      // Enviar email de forma asíncrona si hay email válido
      if (emailCliente && validarEmail(emailCliente)) {
        ventasRepository.enviarBoletaPorEmail(response.boletaId, emailCliente)
          .then(() => {
            addToast('success', 'Boleta enviada por correo');
          })
          .catch((error) => {
            console.error('Error al enviar email:', error);
            addToast('warning', 'Venta registrada pero no se pudo enviar el correo');
          });
      }

      // Éxito
      addToast('success', `Venta registrada exitosamente${imprimir ? ' - Imprimiendo...' : ''}`);
      clearCart();
      setIsFinalizando(false);
      onClose();

      onSaleCompleted?.(response.boletaId, imprimir);
    } catch (error) {
      setIsFinalizando(false);

      // El interceptor de axios-client.ts normaliza todo error a un objeto plano
      // { message, statusCode, fieldErrors, timestamp } sin `.response` (ver
      // apps/web/src/utils/errores.ts). Por eso se lee `statusCode`, no `error.response`.
      const apiError = error as VentaErrorResponse & { statusCode?: number };

      // Suscripción premium expirada (pagos mixtos)
      if (apiError.statusCode === 409) {
        addToast('error', apiError.message || 'Tu suscripción Premium ha expirado. Renueva para seguir usando múltiples medios de pago.');
        return;
      }

      // Errores de validación del backend (400)
      if (apiError.statusCode === 400) {
        if (apiError.fieldErrors && apiError.fieldErrors.length > 0) {
          const MAX_TOASTS = 3;
          apiError.fieldErrors.slice(0, MAX_TOASTS).forEach((fieldError) => {
            addToast('error', fieldError.message);
          });
          const restantes = apiError.fieldErrors.length - MAX_TOASTS;
          if (restantes > 0) {
            addToast('error', `y ${restantes} error${restantes > 1 ? 'es' : ''} más`);
          }
        } else {
          addToast('error', apiError.message || 'Error al procesar la venta');
        }
      } else {
        // Error genérico (red, servidor, etc.)
        addToast('error', apiError.message || 'Error al registrar la venta');
      }
      console.error('Error al crear venta:', error);
    }
  };

  // Mantener ref actualizado en cada render para que el listener de teclado use la versión actual
  handleFinalizarRef.current = handleFinalizar;

  // ============================================
  // FUNCIONES PARA MODO MULTI-PAGO
  // ============================================

  const handleActivarMultiPago = () => {
    // Verificar premium
    if (!session?.hasPremiumAccess) {
      setShowPremiumModal(true);
      return;
    }

    // Activar modo multi-pago
    setIsMultiPaymentMode(true);
    
    // Inicializar con el pago actual como primera línea
    const primerPago: Payment = {
      id: 1,
      methodPayment: tipoPago,
      amount: tipoPago === TipoPago.EFECTIVO ? montoRecibido : totalAPagar.toString(),
      fiadoClienteId: clienteSeleccionado?.clienteId,
      cliente: clienteSeleccionado,
    };
    
    setPayments([primerPago]);
    setNextPaymentId(2);
    
    // Si el primer pago es efectivo, inicializar el monto recibido
    if (tipoPago === TipoPago.EFECTIVO) {
      setMontoRecibidoEfectivo(montoRecibido);
    }
  };

  const handleAgregarPago = () => {
    if (payments.length >= 3) {
      addToast('warning', 'Máximo 3 medios de pago por venta');
      return;
    }

    const nuevoPago: Payment = {
      id: nextPaymentId,
      methodPayment: TipoPago.EFECTIVO,
      amount: '0',
    };

    setPayments([...payments, nuevoPago]);
    setNextPaymentId(nextPaymentId + 1);
  };

  const handleEliminarPago = (id: number) => {
    const nuevosPagos = payments.filter(p => p.id !== id);
    setPayments(nuevosPagos);

    // Si después de eliminar ya no hay pagos de efectivo, resetear el monto recibido
    if (!nuevosPagos.some(p => p.methodPayment === TipoPago.EFECTIVO)) {
      setMontoRecibidoEfectivo('0');
    }

    // Si solo queda 1 pago, podríamos volver al modo single
    // pero es mejor mantener el modo multi para evitar cambios bruscos
  };

  const handleActualizarPago = (id: number, campo: keyof Payment, valor: any) => {
    const nuevoPagos = payments.map(p => {
      if (p.id === id) {
        const updated = { ...p, [campo]: valor };
        
        // Si cambia el método de pago
        if (campo === 'methodPayment') {
          // Si cambia a no FIADO, limpiar cliente
          if (valor !== TipoPago.FIADO) {
            updated.fiadoClienteId = undefined;
            updated.cliente = null;
          }
        }
        
        return updated;
      }
      return p;
    });
    
    setPayments(nuevoPagos);
    
    // Si ya no hay pagos de efectivo después de esta actualización, resetear monto recibido
    if (campo === 'methodPayment' && !nuevoPagos.some(p => p.methodPayment === TipoPago.EFECTIVO)) {
      setMontoRecibidoEfectivo('0');
    }
  };

  const handleSeleccionarClienteParaPago = (paymentId: number, cliente: ClienteFiadoResult) => {
    setPayments(payments.map(p => {
      if (p.id === paymentId) {
        return {
          ...p,
          fiadoClienteId: cliente.clienteId,
          cliente: cliente,
        };
      }
      return p;
    }));
    
    // Reset búsqueda
    resetBusqueda();
    addToast('success', `Cliente ${cliente.nombre} seleccionado`);
  };

  const calcularSumaPagos = (): number => {
    return payments.reduce((sum, p) => {
      const amount = parseInt(p.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const calcularRestante = (): number => {
    return totalAPagar - calcularSumaPagos();
  };

  const handleCompletarRestantePago = (paymentId: number) => {
    const restante = calcularRestante();
    if (restante === 0) return;

    setPayments(payments.map(p => {
      if (p.id === paymentId) {
        const montoActual = parseInt(p.amount) || 0;
        let nuevoMonto = Math.max(0, montoActual + restante);
        
        // Si el pago es en efectivo, aplicar redondeo según ley chilena
        if (p.methodPayment === TipoPago.EFECTIVO) {
          const montoSinRedondear = nuevoMonto;
          nuevoMonto = redondearEfectivo(nuevoMonto);
          
          // Informar al usuario si se aplicó redondeo
          if (montoSinRedondear !== nuevoMonto) {
            addToast('info', `Monto redondeado de $${montoSinRedondear.toLocaleString('es-CL')} a $${nuevoMonto.toLocaleString('es-CL')} según ley de redondeo`);
          }
        }
        
        return { ...p, amount: nuevoMonto.toString() };
      }
      return p;
    }));
  };

  const calcularVueltoEfectivo = (): number => {
    // Calcular la suma de todos los pagos en efectivo
    const totalEfectivo = payments
      .filter(p => p.methodPayment === TipoPago.EFECTIVO)
      .reduce((sum, p) => sum + (parseInt(p.amount) || 0), 0);
    
    if (totalEfectivo === 0) return 0;

    const montoEfectivoRecibido = parseInt(montoRecibidoEfectivo) || 0;
    
    return Math.max(0, montoEfectivoRecibido - totalEfectivo);
  };

  // Verificar si la boleta debería estar deshabilitada
  const isBoletaDisabled = !!(
    kpis?.siiAlertKpi?.isOutOfFolios || 
    (kpis?.siiAlertKpi?.certExpiringSoon && kpis?.siiAlertKpi?.certDaysLeft <= 0) || 
    (kpis?.siiAlertKpi?.foliosExpiringSoon && kpis?.siiAlertKpi?.foliosDaysLeft <= 0)
  );

  // Mensaje descriptivo para boleta deshabilitada
  const getBoletaDisabledMessage = () => {
    const issues = [];
    if (kpis?.siiAlertKpi?.isOutOfFolios) issues.push('sin folios vigentes');
    if (kpis?.siiAlertKpi?.certExpiringSoon && kpis?.siiAlertKpi?.certDaysLeft <= 0) issues.push('certificado expirado');
    if (kpis?.siiAlertKpi?.foliosExpiringSoon && kpis?.siiAlertKpi?.foliosDaysLeft <= 0) issues.push('folios expirados');
    console.log('Boleta deshabilitada por:', issues);
    console.log('KPI SII Alert:', kpis?.siiAlertKpi);
    
    return issues.length > 0 
      ? issues.join(' y ').charAt(0).toUpperCase() + issues.join(' y ').slice(1)
      : 'Sin folios vigentes y certificado expirado';
  };

  // Iconos para medios de pago
  const paymentIcons: Record<TipoPago, JSX.Element> = {
    [TipoPago.EFECTIVO]: <FaMoneyBillWave className="text-3xl" />,
    [TipoPago.DEBITO]: <FaCreditCard className="text-3xl" />,
    [TipoPago.CREDITO]: <SiVisa className="text-3xl" />,
    [TipoPago.TRANSFERENCIA]: <FaExchangeAlt className="text-3xl" />,
    [TipoPago.FIADO]: <FaHandHoldingDollar className="text-3xl" />,
  };

  // Labels para los botones
  const paymentLabels: Record<TipoPago, string> = {
    [TipoPago.EFECTIVO]: 'Efectivo',
    [TipoPago.DEBITO]: 'Débito',
    [TipoPago.CREDITO]: 'Crédito',
    [TipoPago.TRANSFERENCIA]: 'Transferencia',
    [TipoPago.FIADO]: 'Fiado',
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {isFinalizando ? (
          /* Modal de carga durante finalización */
          <div className="flex flex-col items-center justify-center py-20 px-6">
            {/* Spinner animado */}
            <div className="relative mb-8">
              <div className="w-24 h-24 border-8 border-gray-200 border-t-green-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full animate-pulse"></div>
              </div>
            </div>

            {/* Mensaje principal */}
            <h2 className="text-4xl font-bold text-gray-800 mb-3 animate-pulse">
              Finalizando tu venta...
            </h2>

            {/* Mensaje secundario */}
            <p className="text-lg text-gray-600 text-center max-w-md">
              Estamos procesando el pago y actualizando el inventario. 
              Por favor espera un momento.
            </p>

            {/* Barra de progreso animada */}
            <div className="w-full max-w-md mt-8 bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full"
                style={{
                  animation: 'slideProgress 2s ease-in-out infinite',
                }}
              ></div>
            </div>

            <style>{`
              @keyframes slideProgress {
                0% {
                  transform: translateX(-100%);
                }
                50% {
                  transform: translateX(0%);
                }
                100% {
                  transform: translateX(100%);
                }
              }
            `}</style>

            {/* Icono decorativo */}
            <div className="mt-8 text-6xl animate-bounce">
              💰
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-5 py-3 border-b border-bottom-primary border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                  TE ESTÁN PAGANDO: ${totalAPagar.toLocaleString('es-CL')}
                </h2>
                {!isMultiPaymentMode && tipoPago === TipoPago.EFECTIVO && totalAPagar !== total && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    (Total original: ${total.toLocaleString('es-CL')} - Redondeado por ley artículo 12)
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                title="Cerrar"
              >
                <MdClose className="text-2xl text-gray-600" />
              </button>
            </div>

        {/* Body */}
        <div className="px-5 py-4">
          {/* Selección de medio de pago */}
          {!isMultiPaymentMode ? (
            /* Modo Single-Pago: Tarjetas grandes */
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-700 mb-3">
                Selecciona el medio de pago del cliente:
              </h3>

              {/* Grid de botones de pago - Primera fila: 3 botones */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                {[TipoPago.EFECTIVO, TipoPago.DEBITO, TipoPago.CREDITO].map((tipo) => (
                  <button
                    key={tipo}
                    onClick={() => handleTipoPagoClick(tipo)}
                    className={`p-3 rounded-lg border-2 transition-all hover:scale-105 active:scale-95 ${
                      tipoPago === tipo
                        ? 'border-green-500 bg-green-50'
                        : 'bg-white hover:border-blue-400'
                    }`}
                    style={{
                      borderColor: tipoPago === tipo ? undefined : 'var(--checkout-payment-border)',
                    }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className={tipoPago === tipo ? 'text-green-600 text-2xl' : 'text-2xl'}
                        style={{
                          color: tipoPago === tipo ? undefined : 'var(--checkout-payment-icon)',
                        }}
                      >
                        {paymentIcons[tipo]}
                      </div>
                      <span
                        className={`font-semibold text-sm ${
                          tipoPago === tipo ? 'text-green-700' : ''
                        }`}
                        style={{
                          color: tipoPago === tipo ? undefined : 'var(--checkout-payment-text)',
                        }}
                      >
                        {paymentLabels[tipo]}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Segunda fila: 3 botones (Transferencia, Fiado, Multi-Pago) */}
              <div className="grid grid-cols-3 gap-3">
                {[TipoPago.TRANSFERENCIA, TipoPago.FIADO].map((tipo) => (
                  <button
                    key={tipo}
                    onClick={() => handleTipoPagoClick(tipo)}
                    className={`p-3 rounded-lg border-2 transition-all hover:scale-105 active:scale-95 ${
                      tipoPago === tipo
                        ? 'border-green-500 bg-green-50'
                        : 'bg-white hover:border-blue-400'
                    }`}
                    style={{
                      borderColor: tipoPago === tipo ? undefined : 'var(--checkout-payment-border)',
                    }}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className={tipoPago === tipo ? 'text-green-600 text-2xl' : 'text-2xl'}
                        style={{
                          color: tipoPago === tipo ? undefined : 'var(--checkout-payment-icon)',
                        }}
                      >
                        {paymentIcons[tipo]}
                      </div>
                      <span
                        className={`font-semibold text-sm ${
                          tipoPago === tipo ? 'text-green-700' : ''
                        }`}
                        style={{
                          color: tipoPago === tipo ? undefined : 'var(--checkout-payment-text)',
                        }}
                      >
                        {paymentLabels[tipo]}
                      </span>
                    </div>
                  </button>
                ))}
                
                {/* Botón Multi-Pago */}
                <button
                  onClick={handleActivarMultiPago}
                  className="p-3 rounded-lg border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-all hover:scale-105 active:scale-95"
                >
                  <div className="flex flex-col items-center gap-1 relative">
                    <div className="text-2xl text-purple-600">
                      <GiWallet />
                    </div>
                    <span className="font-semibold text-sm text-purple-700">
                      Pagos Mixtos
                    </span>
                    <span className="absolute -top-1 -right-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                      <FaCrown className="text-[8px]" />
                      Premium
                    </span>
                  </div>
                </button>
              </div>
            </div>
          ) : (
            /* Modo Multi-Pago con botón para volver */
            <>
              <div className="mb-4 flex justify-center">
                <button
                  onClick={() => {
                    setIsMultiPaymentMode(false);
                    setPayments([]);
                    setNextPaymentId(1);
                    setMontoRecibidoEfectivo('0');
                    addToast('info', 'Volviste al modo de pago simple');
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-all"
                >
                  <MdClose className="text-lg" />
                  <span className="text-sm font-semibold text-gray-700">
                    Volver a modo simple
                  </span>
                </button>
              </div>

              {/* Modo Multi-Pago: Lista de filas */}
              <div className="mb-4">
                <h3 className="text-base font-semibold text-gray-700 mb-3">
                  Medios de pago ({payments.length}/3):
                </h3>

                <div className="space-y-3">
                  {payments.map((pago, index) => (
                  <div key={pago.id} className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-3">
                      {/* Número de pago */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>

                      {/* Selector de método */}
                      <select
                        value={pago.methodPayment}
                        onChange={(e) => handleActualizarPago(pago.id, 'methodPayment', e.target.value as TipoPago)}
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 bg-white font-medium text-gray-700"
                      >
                        <option value={TipoPago.EFECTIVO}>💵 Efectivo</option>
                        <option value={TipoPago.DEBITO}>💳 Débito</option>
                        <option value={TipoPago.CREDITO}>💳 Crédito</option>
                        <option value={TipoPago.TRANSFERENCIA}>↔️ Transferencia</option>
                        <option value={TipoPago.FIADO}>🤝 Fiado</option>
                      </select>

                      {/* Input de monto */}
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg font-semibold text-gray-600">
                          $
                        </span>
                        <input
                          type="text"
                          value={formatearMonto(pago.amount)}
                          onChange={(e) => {
                            const soloNumeros = e.target.value.replace(/\D/g, '');
                            handleActualizarPago(pago.id, 'amount', soloNumeros);
                          }}
                          onFocus={(e) => e.target.select()}
                          className={`w-full pl-8 pr-3 py-2 border-2 rounded-lg focus:outline-none font-semibold text-gray-700 ${
                            pago.methodPayment === TipoPago.EFECTIVO && 
                            parseInt(pago.amount) % 10 !== 0 && 
                            parseInt(pago.amount) > 0
                              ? 'border-red-500 focus:border-red-600'
                              : 'border-gray-300 focus:border-blue-400'
                          }`}
                          placeholder="0"
                          inputMode="numeric"
                        />
                      </div>

                      {/* Botón Completar restante */}
                      <button
                        onClick={() => handleCompletarRestantePago(pago.id)}
                        className="px-4 py-2 bg-white text-[#43BFFF] border-2 border-[#43BFFF] text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
                        title={pago.methodPayment === TipoPago.EFECTIVO ? 'Completa y redondea según ley' : 'Completa el restante'}
                      >
                        {pago.methodPayment === TipoPago.EFECTIVO ? 'Ajustar y redondear' : 'Completar restante'}
                      </button>

                      {/* Botón eliminar (solo si hay 2+ pagos) */}
                      {payments.length > 1 && (
                        <button
                          onClick={() => handleEliminarPago(pago.id)}
                          className="flex-shrink-0 p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                          title="Eliminar pago"
                        >
                          <MdDelete className="text-xl" />
                        </button>
                      )}
                    </div>

                    {/* Cliente fiado si aplica */}
                    {pago.methodPayment === TipoPago.FIADO && (
                      <div className="mt-3 pt-3 border-t border-gray-300">
                        {pago.cliente ? (
                          <div className="flex items-center justify-between p-2 bg-green-50 border border-green-300 rounded-lg">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 text-sm">{pago.cliente.nombre}</p>
                              <p className="text-xs text-gray-600">
                                Tel: +56 {pago.cliente.telefono} | RUT: {formatRut(`${pago.cliente.rut}${pago.cliente.dv}`)}
                              </p>
                            </div>
                            <button
                              onClick={() => handleActualizarPago(pago.id, 'cliente', null)}
                              className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                            >
                              Cambiar
                            </button>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Seleccionar cliente fiado <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <div className="border-2 border-orange-400 rounded-lg overflow-hidden bg-white">
                                <div className="flex items-center">
                                  <div className="flex-1 relative">
                                    <MdSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-base" />
                                    <input
                                      type="text"
                                      value={busquedaTexto}
                                      onChange={handleBusquedaTextoChange}
                                      className="w-full pl-8 pr-2 py-1.5 focus:outline-none text-gray-600 text-xs"
                                      placeholder="Buscar por nombre, RUT o teléfono..."
                                    />
                                  </div>
                                  <button
                                    onClick={() => setMostrarModalCrear(true)}
                                    className="px-3 py-1.5 bg-green-500 text-white hover:bg-green-600 transition-colors text-xs font-medium"
                                  >
                                    + Nuevo
                                  </button>
                                </div>
                              </div>

                              {/* Dropdown de resultados */}
                              {busquedaTexto.length >= 3 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden max-h-40 overflow-y-auto">
                                  {buscarClientesMutation.isPending ? (
                                    <div className="p-2 text-center text-gray-500 text-xs">
                                      <p>Buscando...</p>
                                    </div>
                                  ) : clientesBuscados.length > 0 ? (
                                    clientesBuscados.map((cliente) => (
                                      <button
                                        key={cliente.clienteId}
                                        onClick={() => handleSeleccionarClienteParaPago(pago.id, cliente)}
                                        className="w-full p-2 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-b-0"
                                      >
                                        <p className="font-semibold text-gray-900 text-xs">{cliente.alias || cliente.nombre}</p>
                                        <p className="text-[10px] text-gray-500">
                                          RUT: {cliente.rut ? formatRut(`${cliente.rut}${cliente.dv}`) : '-'} | +56 {cliente.telefono}
                                        </p>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-2 text-center text-gray-500">
                                      <p className="text-xs">No se encontraron clientes</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Botón agregar otro pago */}
              {payments.length < 3 && (
                <button
                  onClick={handleAgregarPago}
                  className="mt-3 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all font-medium text-sm flex items-center justify-center gap-2"
                >
                  <MdAdd className="text-lg" />
                  Agregar otro pago
                </button>
              )}

              {/* Indicador de restante */}
              <div className="mt-3 p-3 rounded-lg border-2" style={{
                borderColor: Math.abs(calcularRestante()) <= 2 ? '#10B981' : '#EF4444',
                backgroundColor: Math.abs(calcularRestante()) <= 2 ? '#D1FAE5' : '#FEE2E2',
              }}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-700 text-sm">
                    Restante por asignar:
                  </span>
                  <span className="font-bold text-lg" style={{
                    color: Math.abs(calcularRestante()) <= 2 ? '#10B981' : '#EF4444',
                  }}>
                    ${calcularRestante().toLocaleString('es-CL')}
                  </span>
                </div>
              </div>

              {/* Campo de monto recibido en efectivo (si existe línea de efectivo) */}
              {payments.some(p => p.methodPayment === TipoPago.EFECTIVO) && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Recibo en efectivo
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg font-semibold text-gray-600">
                      $
                    </span>
                    <input
                      type="text"
                      value={formatearMonto(montoRecibidoEfectivo)}
                      onChange={(e) => {
                        const soloNumeros = e.target.value.replace(/\D/g, '');
                        setMontoRecibidoEfectivo(soloNumeros);
                      }}
                      onFocus={(e) => e.target.select()}
                      className="w-full pl-8 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 font-semibold text-gray-700"
                      placeholder="0"
                      inputMode="numeric"
                    />
                  </div>
                  
                  {/* Vuelto */}
                  {parseInt(montoRecibidoEfectivo) > 0 && (
                    <div className="mt-2 p-2 bg-pink-50 rounded-lg border border-pink-200">
                      <p className="text-sm">
                        <span className="font-semibold text-gray-700">Vuelto: </span>
                        <span className="font-bold" style={{ color: '#EC4899' }}>
                          ${calcularVueltoEfectivo().toLocaleString('es-CL')}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
            </>
          )}

          {/* Campo de monto recibido (solo para efectivo en modo single) */}
          {!isMultiPaymentMode && tipoPago === TipoPago.EFECTIVO && (
            <div className="mb-4">
              <label className="block text-base font-semibold text-gray-700 mb-2">
                Recibo
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xl font-semibold text-gray-600">
                  $
                </span>
                <input
                  ref={montoRecibidoInputRef}
                  type="text"
                  value={formatearMonto(montoRecibido)}
                  onChange={handleMontoRecibidoChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-full pl-9 pr-4 py-3 text-xl font-semibold border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="0"
                  inputMode="numeric"
                />
              </div>

              {/* Vuelto */}
              {parseFloat(montoRecibido) >= 0 && (
                <div className="mt-2">
                  <p className="text-xl font-bold" style={{ color: vuelto >= 0 ? '#EC4899' : '#EF4444' }}>
                    Vuelto: ${Math.max(0, vuelto).toLocaleString('es-CL')}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Selección de tipo de recibo */}
          <div className="mb-4">
            <h3 className="text-base font-semibold text-gray-700 mb-3">
              Selecciona el recibo:
            </h3>

            <div className="grid grid-cols-2 gap-3">
              {/* Voucher - Siempre visible */}
              <button
                onClick={() => setTipoRecibo('VOUCHER')}
                className={`p-3 rounded-lg border-2 transition-all hover:scale-105 active:scale-95 ${
                  tipoRecibo === 'VOUCHER'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <span
                    className={`text-base font-semibold ${
                      tipoRecibo === 'VOUCHER' ? 'text-green-700' : 'text-gray-700'
                    }`}
                  >
                    Voucher
                  </span>
                  <span className="text-xs text-gray-500">
                    (no válido como boleta)
                  </span>
                </div>
              </button>

              {/* Boleta - Solo si taxpayer existe */}
              {session?.taxpayer && (
                <button
                  onClick={() => !isBoletaDisabled && setTipoRecibo('BOLETA')}
                  disabled={isBoletaDisabled}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    isBoletaDisabled 
                      ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50' 
                      : tipoRecibo === 'BOLETA'
                      ? 'border-green-500 bg-green-50 hover:scale-105 active:scale-95'
                      : 'border-gray-300 bg-white hover:border-gray-400 hover:scale-105 active:scale-95'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <span
                      className={`text-base font-semibold ${
                        isBoletaDisabled
                          ? 'text-gray-400'
                          : tipoRecibo === 'BOLETA' 
                          ? 'text-green-700' 
                          : 'text-gray-700'
                      }`}
                    >
                      Boleta
                    </span>
                    <span className={`text-xs text-center ${
                      isBoletaDisabled ? 'text-red-500' : 'text-gray-500'
                    }`}>
                      {isBoletaDisabled 
                        ? getBoletaDisabledMessage()
                        : 'Boleta electrónica'
                      }
                    </span>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Gestión de clientes para FIADO (solo modo single) */}
          {!isMultiPaymentMode && tipoPago === TipoPago.FIADO && (
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-700 mb-3">
                Seleccionar Cliente
              </h3>

              {/* Cliente seleccionado */}
              {clienteSeleccionado ? (
                <div className="p-3 bg-green-50 border-2 border-green-300 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">{clienteSeleccionado.alias || clienteSeleccionado.nombre}</p>
                      <p className="text-xs text-gray-600">
                        Tel: +56 {clienteSeleccionado.telefono} | RUT: {formatRut(`${clienteSeleccionado.rut}${clienteSeleccionado.dv}`)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {clienteSeleccionado.permiteEditar && (
                        <button
                          onClick={handleEditarCliente}
                          className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                        >
                          <MdEdit className="text-base" /> Editar
                        </button>
                      )}
                      <button
                        onClick={handleCambiarCliente}
                        className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                      >
                        Cambiar
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Búsqueda unificada de clientes */
                <>
                  <div className="relative">
                    <div className="border-2 border-green-400 rounded-lg overflow-hidden bg-white">
                      {/* Input de búsqueda con botón Nuevo */}
                      <div className="flex items-center">
                        <div className="flex-1 relative">
                          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                          <input
                            type="text"
                            value={busquedaTexto}
                            onChange={handleBusquedaTextoChange}
                            className="w-full pl-10 pr-3 py-2 focus:outline-none text-gray-600 text-sm"
                            placeholder="Buscar cliente por nombre, RUT(11.222.333-4) o teléfono..."
                          />
                        </div>
                        <button
                          onClick={() => setMostrarModalCrear(true)}
                          className="px-4 py-2 bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-1 font-medium text-sm"
                        >
                          <span className="text-lg">+</span>
                          Nuevo
                        </button>
                      </div>
                    </div>

                    {/* Información de que debe ingressar al menos 3 caracteres */}
                    {busquedaTexto.length > 0 && busquedaTexto.length < 3 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                        <p className="p-3 text-center text-gray-500 text-sm">
                          Ingresa al menos 3 caracteres para buscar
                        </p>
                      </div>
                    )}

                    {/* Resultados de búsqueda - Dropdown absoluto */}
                    {busquedaTexto.length >= 3 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                        {buscarClientesMutation.isPending ? (
                          <div className="p-3 text-center text-gray-500 text-sm">
                            <p>Buscando...</p>
                          </div>
                        ) : clientesBuscados.length > 0 ? (
                          <div className="max-h-48 overflow-y-auto">
                            {clientesBuscados.map((cliente) => (
                              <button
                                key={cliente.clienteId}
                                onClick={() => handleSeleccionarCliente(cliente)}
                                className="w-full p-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-3"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate text-sm">{cliente.alias || cliente.nombre}</p>
                                  <p className="text-xs text-gray-500">
                                    RUT: { cliente.rut 
                                      ? formatRut(`${cliente.rut}${cliente.dv}`)
                                      : '-'
                                    }
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-xs text-gray-500">
                                    +56 {cliente.telefono.slice(0, 1)} {cliente.telefono.slice(1, 5)} {cliente.telefono.slice(5)}
                                  </p>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="p-3 text-center text-gray-500">
                            <p className="text-xs">No se encontraron clientes</p>
                            <button
                              onClick={() => setMostrarModalCrear(true)}
                              className="mt-1 text-green-600 hover:text-green-700 text-xs font-medium"
                            >
                              + Crear nuevo cliente
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Advertencia de cliente obligatorio */}
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-300 rounded-lg">
                    <p className="text-xs text-orange-700 text-center font-medium">
                      ⚠️ Debe seleccionar un cliente para continuar con la venta fiada
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Acordeón de email (opcional) */}
          <div className="mb-4">
            <button
              onClick={() => setShowEmailInput(!showEmailInput)}
              className="w-full flex items-center justify-between py-2 px-3 rounded-lg text-gray-700 bg-gray-50 border-2 border-gray-300 hover:bg-gray-100 hover:border-gray-400 transition-colors"
            >
              <span className="text-sm font-medium">Correo electrónico (opcional)</span>
              <MdKeyboardArrowDown
                className={`text-xl transition-transform duration-300 ${showEmailInput ? 'rotate-180' : ''}`}
              />
            </button>
            
            <div
              className={`overflow-hidden transition-all duration-300 ${showEmailInput ? 'max-h-28 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
            >
              <input
                type="email"
                value={emailCliente}
                onChange={handleEmailChange}
                className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                  emailError
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder="cliente@ejemplo.com"
              />
              {emailError && (
                <p className="text-red-500 text-xs mt-1">{emailError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer con botones */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-2xl sticky bottom-0">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleFinalizar(false)}
              disabled={
                isMultiPaymentMode
                  ? (
                    payments.length === 0 ||
                    Math.abs(calcularRestante()) > 2 ||
                    payments.some(p => p.methodPayment === TipoPago.FIADO && !p.fiadoClienteId)
                  )
                  : (tipoPago === TipoPago.FIADO && !clienteSeleccionado)
              }
              className="py-3 rounded-lg font-bold text-lg text-white transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ backgroundColor: '#43BFFF' }}
            >
              Finalizar
            </button>
            <button
              onClick={() => handleFinalizar(true)}
              disabled={
                isMultiPaymentMode
                  ? (
                    payments.length === 0 ||
                    Math.abs(calcularRestante()) > 2 ||
                    payments.some(p => p.methodPayment === TipoPago.FIADO && !p.fiadoClienteId)
                  )
                  : (tipoPago === TipoPago.FIADO && !clienteSeleccionado)
              }
              className="py-3 rounded-lg font-bold text-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ backgroundColor: '#FACC15', color: '#11194F' }}
            >
              Imprimir y Finalizar
            </button>
          </div>
        </div>
          </>
        )}
      </div>

      {/* Modal de Crear Cliente */}
      {mostrarModalCrear && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                Crear Cliente
              </h2>
              <button
                onClick={() => setMostrarModalCrear(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <MdClose className="text-3xl text-gray-600" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formNombre}
                  onChange={(e) => setFormNombre(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="Nombre completo"
                />
                {formNombre && formNombre.length < 3 && (
                  <p className="text-xs text-red-500 mt-1">Debe tener al menos 3 caracteres</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border border-r-0 border-gray-300 rounded-l-lg">
                    +56
                  </span>
                  <input
                    type="text"
                    value={formTelefono}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormTelefono(value.slice(0, 9));
                    }}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-r-lg focus:outline-none focus:border-blue-400"
                    placeholder="9 dígitos"
                    maxLength={9}
                    inputMode="numeric"
                  />
                </div>
                {formTelefono && formTelefono.length !== 9 && (
                  <p className="text-xs text-red-500 mt-1">Debe tener 9 dígitos</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RUT <span className="text-gray-500 text-xs">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={formRutFormateado}
                  onChange={handleFormRutChange}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="12.345.678-9"
                  maxLength={12}
                />
                {formRut && !validateRut(formRut) && (
                  <p className="text-xs text-red-500 mt-1">RUT inválido</p>
                )}
              </div>

              <button
                onClick={handleCrearCliente}
                disabled={
                  crearClienteMutation.isPending ||
                  !formNombre.trim() ||
                  formNombre.trim().length < 3 ||
                  formTelefono.length !== 9 ||
                  (!!formRut && !validateRut(formRut))
                }
                className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {crearClienteMutation.isPending ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <MdCheck className="text-xl" />
                    Crear Cliente
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Cliente */}
      {mostrarModalEditar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{ color: '#11194F' }}>
                Editar Cliente
              </h2>
              <button
                onClick={() => {
                  setMostrarModalEditar(false);
                  resetFormulario();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <MdClose className="text-3xl text-gray-600" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formNombre}
                  onChange={(e) => setFormNombre(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="Nombre completo"
                />
                {formNombre && formNombre.length < 3 && (
                  <p className="text-xs text-red-500 mt-1">Debe tener al menos 3 caracteres</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border border-r-0 border-gray-300 rounded-l-lg">
                    +56
                  </span>
                  <input
                    type="text"
                    value={formTelefono}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setFormTelefono(value.slice(0, 9));
                    }}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-r-lg focus:outline-none focus:border-blue-400"
                    placeholder="9 dígitos"
                    maxLength={9}
                    inputMode="numeric"
                  />
                </div>
                {formTelefono && formTelefono.length !== 9 && (
                  <p className="text-xs text-red-500 mt-1">Debe tener 9 dígitos</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RUT <span className="text-gray-500 text-xs">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={formRutFormateado}
                  onChange={handleFormRutChange}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="12.345.678-9"
                  maxLength={12}
                />
                {formRut && !validateRut(formRut) && (
                  <p className="text-xs text-red-500 mt-1">RUT inválido</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alias <span className="text-gray-500 text-xs">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={formAlias}
                  onChange={(e) => setFormAlias(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                  placeholder="Nombre alternativo del cliente"
                  maxLength={100}
                />
              </div>

              <button
                onClick={handleActualizarCliente}
                disabled={
                  actualizarClienteMutation.isPending ||
                  !formNombre.trim() ||
                  formNombre.trim().length < 3 ||
                  formTelefono.length !== 9 ||
                  (!!formRut && !validateRut(formRut))
                }
                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {actualizarClienteMutation.isPending ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <MdCheck className="text-xl" />
                    Guardar Cambios
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Premium */}
      <PremiumRequiredModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        featureTitle="Cobrar con múltiples medios de pago"
        featureDescription="Esta es una función Premium que te permite dividir el cobro de una venta en hasta 3 medios de pago diferentes (por ejemplo: parte en efectivo, parte con tarjeta de débito y parte fiado)."
        benefits={[
          'Mayor flexibilidad para tus clientes',
          'Combina efectivo, tarjetas y crédito fiado',
          'Control total sobre cada medio de pago',
        ]}
        additionalNote="Las ventas multi-pago que ya creaste pueden seguir editándose normalmente, pero no podrás crear nuevas sin Premium."
      />
    </div>
  );
};
